/**
 * PermisFeuForm.jsx — Formulaire fidèle au template "Permis de Feu" Lafarge.
 * Reproduit visuellement les blocs du template Excel :
 *   - En-tête : Dates, Description, Localisation, Entreprise, N°PP
 *   - Type de travail par point chaud (4 cases multiples)
 *   - Matériel utilisé (4 cases multiples) + Vérifié + Bouteilles arrimées
 *   - Risques particuliers (ATEX + Réservoir)
 *   - PERMIS DE FEU COMPLEXE : Moyens d'extinction & risques
 *   - Mesures supplémentaires zones ATEX
 *   - Désactivation système détection incendie
 *
 * Utilise les valeurs depuis permit.extra.* et délègue les modifications à setExtra(k,v)
 */
import { Flame, Check } from 'lucide-react';
import FormationPicker from '@/components/FormationPicker';

function Checkbox({ checked, onChange, label, testid, className = '' }) {
  return (
    <label className={`flex items-start gap-2 cursor-pointer text-xs ${className}`} data-testid={testid}>
      <input
        type="checkbox"
        checked={!!checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 w-4 h-4 accent-[#DA291C] flex-shrink-0"
      />
      <span className="leading-snug">{label}</span>
    </label>
  );
}

function YesNoNa({ value, onChange, testid, options = ['OUI', 'NON', 'N/A'] }) {
  return (
    <div className="inline-flex border border-stone-300" data-testid={testid}>
      {options.map((opt) => (
        <button
          key={opt}
          type="button"
          onClick={() => onChange(opt)}
          className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider transition-colors ${
            value === opt
              ? opt === 'OUI'
                ? 'bg-green-600 text-white'
                : opt === 'NON'
                  ? 'bg-[#DA291C] text-white'
                  : 'bg-stone-500 text-white'
              : 'bg-white text-stone-600 hover:bg-stone-100'
          }`}
        >
          {opt}
        </button>
      ))}
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <div className="bg-[#09090B] text-white text-[11px] font-bold uppercase tracking-[0.2em] px-3 py-2">
      {children}
    </div>
  );
}

export default function PermisFeuForm({ extra, setExtra, dateDebut, dateFin, titre, lieu, entreprise, compact = false, siteCode = '' }) {
  // Convertit l'extra OUI/NON/N-A en booléen pour les YesNo (pour stockage interne en boolean)
  const yn = (k, val) => setExtra(k, val === 'OUI');
  const getYn = (k, defaultVal = '') => {
    const v = extra[k];
    if (v === true || v === 'OUI') return 'OUI';
    if (v === false || v === 'NON') return 'NON';
    if (v === 'N/A') return 'N/A';
    return defaultVal;
  };

  return (
    <div className="space-y-4 border-2 border-[#DA291C] bg-white" data-testid="permis-feu-form">
      {/* HEADER */}
      <div className="bg-[#DA291C] text-white px-4 py-3 flex items-center gap-3">
        <Flame className="w-6 h-6" strokeWidth={2.5} />
        <div>
          <div className="text-2xl font-display font-black tracking-tighter">PERMIS DE FEU</div>
          <div className="text-[10px] uppercase tracking-wider opacity-90">
            L'opérateur doit être en possession de ce permis pour commencer ses travaux.
          </div>
        </div>
      </div>

      <div className="px-4 pb-4 space-y-4">
        {/* En-tête : Dates / Description / Localisation / Entreprise — masqués en mode compact (enfant d'AT, hérités) */}
        {!compact && (
        <div>
          <div className="text-[10px] font-bold uppercase tracking-wider text-stone-600 mb-1">
            Date / heure et durée de validité du permis
          </div>
          <div className="grid grid-cols-2 gap-3 text-xs">
            <div className="border border-stone-300 bg-stone-50 px-2 py-1.5">
              <span className="font-bold text-stone-500">Début : </span>{dateDebut || '—'}
            </div>
            <div className="border border-stone-300 bg-stone-50 px-2 py-1.5">
              <span className="font-bold text-stone-500">Fin : </span>{dateFin || '—'}
            </div>
          </div>
          <Checkbox
            checked={extra.duree_journee}
            onChange={(v) => setExtra('duree_journee', v)}
            label="Le travail dure la journée"
            testid="pf-duree-journee"
            className="mt-2"
          />
        </div>
        )}

        {/* Description + N° PP — en mode compact on garde uniquement N° PP (spécifique au PF) */}
        {compact ? (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-stone-600 mb-1">N° PP <span className="font-normal italic normal-case text-[9px]">(si sous-traité)</span></div>
              <input
                type="text"
                value={extra.pp_numero || ''}
                onChange={(e) => setExtra('pp_numero', e.target.value)}
                data-testid="pf-pp-numero"
                placeholder="Ex. PP-2026-0123"
                className="w-full px-2 py-1.5 text-xs border border-stone-300 focus:border-[#09090B] outline-none"
              />
            </div>
            <div>
              <Checkbox
                checked={extra.duree_journee}
                onChange={(v) => setExtra('duree_journee', v)}
                label="Le travail dure la journée"
                testid="pf-duree-journee"
                className="pt-6"
              />
            </div>
          </div>
        ) : (
        <div className="grid grid-cols-3 gap-3">
          <div className="col-span-2">
            <div className="text-[10px] font-bold uppercase tracking-wider text-stone-600 mb-1">Description du travail à effectuer</div>
            <div className="border border-stone-300 bg-stone-50 px-2 py-1.5 text-xs min-h-[2.5rem]">{titre || '—'}</div>
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-stone-600 mb-1">N° PP <span className="font-normal italic normal-case text-[9px]">(si sous-traité)</span></div>
            <input
              type="text"
              value={extra.pp_numero || ''}
              onChange={(e) => setExtra('pp_numero', e.target.value)}
              data-testid="pf-pp-numero"
              placeholder="Ex. PP-2026-0123"
              className="w-full px-2 py-1.5 text-xs border border-stone-300 focus:border-[#09090B] outline-none"
            />
          </div>
        </div>
        )}

        {/* Localisation + Entreprise — masqués en mode compact (hérités) */}
        {!compact && (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-stone-600 mb-1">Localisation du travail</div>
            <div className="border border-stone-300 bg-stone-50 px-2 py-1.5 text-xs">{lieu || '—'}</div>
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-stone-600 mb-1">Entreprise</div>
            <div className="border border-stone-300 bg-stone-50 px-2 py-1.5 text-xs">{entreprise || 'Travaux internes'}</div>
          </div>
        </div>
        )}

        {/* Type de travail par point chaud */}
        <div>
          <SectionTitle>Type de travail par point chaud <span className="font-normal normal-case italic text-[9px]">(Générateurs d'étincelles ou surfaces chaudes)</span></SectionTitle>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 p-3 border border-stone-200 border-t-0 bg-amber-50/30">
            <Checkbox checked={extra.pc_soudage} onChange={(v) => setExtra('pc_soudage', v)} label="Soudage" testid="pf-pc-soudage" />
            <Checkbox checked={extra.pc_meulage} onChange={(v) => setExtra('pc_meulage', v)} label="Meulage" testid="pf-pc-meulage" />
            <Checkbox checked={extra.pc_tronconnage} onChange={(v) => setExtra('pc_tronconnage', v)} label="Tronçonnage" testid="pf-pc-tronconnage" />
            <Checkbox checked={extra.pc_decoupage} onChange={(v) => setExtra('pc_decoupage', v)} label="Découpage" testid="pf-pc-decoupage" />
          </div>
        </div>

        {/* Matériel utilisé */}
        <div>
          <SectionTitle>Matériel utilisé</SectionTitle>
          <div className="border border-stone-200 border-t-0 bg-stone-50/50">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 p-3">
              <Checkbox checked={extra.mat_poste_souder} onChange={(v) => setExtra('mat_poste_souder', v)} label="Poste à souder" testid="pf-mat-poste-souder" />
              <Checkbox checked={extra.mat_chalumeau} onChange={(v) => setExtra('mat_chalumeau', v)} label="Chalumeau" testid="pf-mat-chalumeau" />
              <Checkbox checked={extra.mat_meuleuse} onChange={(v) => setExtra('mat_meuleuse', v)} label="Meuleuse" testid="pf-mat-meuleuse" />
              <Checkbox checked={extra.mat_tronconneuse} onChange={(v) => setExtra('mat_tronconneuse', v)} label="Tronçonneuse" testid="pf-mat-tronconneuse" />
            </div>
            <div className="px-3 py-2 border-t border-stone-200 space-y-1.5">
              <div className="flex items-center justify-between gap-3">
                <span className="text-xs">Le matériel a été vérifié et est en bon état</span>
                <YesNoNa value={getYn('mat_verifie', 'OUI')} onChange={(v) => yn('mat_verifie', v)} testid="pf-mat-verifie" options={['OUI', 'NON']} />
              </div>
              <div className="flex items-center justify-between gap-3">
                <span className="text-xs">Les bouteilles sont arrimées si présentes</span>
                <YesNoNa value={getYn('mat_bouteilles_arrimees', 'N/A')} onChange={(v) => setExtra('mat_bouteilles_arrimees', v)} testid="pf-mat-bouteilles" />
              </div>
            </div>
          </div>
        </div>

        {/* Risques particuliers */}
        <div>
          <SectionTitle>Risques particuliers liés à l'environnement de travail et moyens de prévention</SectionTitle>
          <div className="border border-stone-200 border-t-0 p-3 bg-orange-50/30 space-y-2">
            <div className="flex items-center justify-between gap-3">
              <span className="text-xs font-semibold">Zones ATEX</span>
              <YesNoNa value={getYn('risque_atex', 'NON')} onChange={(v) => yn('risque_atex', v)} testid="pf-risque-atex" options={['OUI', 'NON']} />
            </div>
            <div className="flex items-center justify-between gap-3">
              <span className="text-xs font-semibold">Proximité de réservoir et/ou canalisation (Gaz, produits inflammables…)</span>
              <YesNoNa value={getYn('risque_reservoir', 'NON')} onChange={(v) => yn('risque_reservoir', v)} testid="pf-risque-reservoir" options={['OUI', 'NON']} />
            </div>
          </div>
        </div>

        {/* PERMIS DE FEU COMPLEXE — Moyens d'extinction */}
        <div>
          <SectionTitle>Permis de Feu Complexe — Moyens de prévention</SectionTitle>
          <div className="border border-stone-200 border-t-0">
            <div className="grid md:grid-cols-2 gap-x-4">
              <div className="p-3 space-y-2 border-b md:border-b-0 md:border-r border-stone-200">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-xs font-semibold">Mise en place de moyens d'extinction</span>
                  <YesNoNa value={getYn('ext_moyens_extinction')} onChange={(v) => setExtra('ext_moyens_extinction', v === 'OUI')} testid="pf-ext-moyens" options={['OUI']} />
                </div>
                <input
                  type="text"
                  value={extra.ext_extinction_precision || ''}
                  onChange={(e) => setExtra('ext_extinction_precision', e.target.value)}
                  placeholder="Préciser…"
                  data-testid="pf-ext-precision"
                  className="w-full px-2 py-1 text-xs border border-stone-300 focus:border-[#09090B] outline-none"
                />
                <div className="flex items-center justify-between gap-3">
                  <span className="text-xs">Matériau combustible dégagé dans un rayon de 10m</span>
                  <YesNoNa value={getYn('ext_materiau_combustible', 'N/A')} onChange={(v) => setExtra('ext_materiau_combustible', v)} testid="pf-ext-combustible" />
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span className="text-xs">Bâche ignifugée sur combustible ou panneau de protection ignifugé</span>
                  <YesNoNa value={getYn('ext_bache_ignifugee', 'N/A')} onChange={(v) => setExtra('ext_bache_ignifugee', v)} testid="pf-ext-bache" />
                </div>
              </div>
              <div className="p-3 space-y-2">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-xs">Réservoir/Canalisation : Vidange et dégazage des volumes creux</span>
                  <YesNoNa value={getYn('ext_vidange_degazage', 'N/A')} onChange={(v) => setExtra('ext_vidange_degazage', v)} testid="pf-ext-vidange" />
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span className="text-xs">Présence d'un certificat de dégazage</span>
                  <YesNoNa value={getYn('ext_certificat_degazage', 'N/A')} onChange={(v) => setExtra('ext_certificat_degazage', v)} testid="pf-ext-certificat" />
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span className="text-xs">Port d'EPI ignifugés et renforcés si soudure</span>
                  <YesNoNa value={getYn('ext_epi_ignifuges', 'N/A')} onChange={(v) => setExtra('ext_epi_ignifuges', v)} testid="pf-ext-epi" />
                </div>
                <div className="flex items-center justify-between gap-3 bg-yellow-50 px-2 -mx-2 py-1">
                  <span className="text-xs font-bold">Balisage de la zone <span className="italic font-normal text-[10px]">(Obligatoire)</span></span>
                  <YesNoNa value={getYn('ext_balisage')} onChange={(v) => setExtra('ext_balisage', v === 'OUI')} testid="pf-ext-balisage" options={['OUI']} />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ATEX — Moyens supplémentaires */}
        {extra.risque_atex && (
          <div data-testid="pf-atex-extra">
            <SectionTitle>Si intervention en zones ATEX : Moyens de prévention supplémentaires</SectionTitle>
            <div className="border border-stone-200 border-t-0 grid md:grid-cols-2 gap-x-4 bg-purple-50/30">
              <div className="p-3 space-y-2 border-b md:border-b-0 md:border-r border-stone-200">
                <div className="text-[10px] font-bold uppercase tracking-wider text-stone-500 mb-1">Zone Gaz / Vapeurs / Brouillard</div>
                <div className="flex items-center justify-between gap-3">
                  <span className="text-xs">Zone identifiée</span>
                  <YesNoNa value={getYn('atex_gaz', 'NON')} onChange={(v) => setExtra('atex_gaz', v === 'OUI')} testid="pf-atex-gaz" options={['OUI', 'NON']} />
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span className="text-xs">Neutralisation de la zone (consignation)</span>
                  <YesNoNa value={getYn('atex_neutralisation', 'N/A')} onChange={(v) => setExtra('atex_neutralisation', v)} testid="pf-atex-neutralisation" />
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span className="text-xs">Ventilation supplémentaire par apport d'air ext.</span>
                  <YesNoNa value={getYn('atex_ventilation', 'N/A')} onChange={(v) => setExtra('atex_ventilation', v)} testid="pf-atex-ventilation" />
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span className="text-xs">Inertage (risque anoxie/asphyxie)</span>
                  <YesNoNa value={getYn('atex_inertage_2', 'N/A')} onChange={(v) => setExtra('atex_inertage_2', v)} testid="pf-atex-inertage2" />
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span className="text-xs">Contrôle de l'atmosphère (Explosimètre)</span>
                  <YesNoNa value={getYn('atex_explosimetre', 'N/A')} onChange={(v) => setExtra('atex_explosimetre', v)} testid="pf-atex-explosimetre" />
                </div>
              </div>
              <div className="p-3 space-y-2">
                <div className="text-[10px] font-bold uppercase tracking-wider text-stone-500 mb-1">Zone Poussières</div>
                <div className="flex items-center justify-between gap-3">
                  <span className="text-xs">Zone identifiée</span>
                  <YesNoNa value={getYn('atex_poussieres', 'NON')} onChange={(v) => setExtra('atex_poussieres', v === 'OUI')} testid="pf-atex-poussieres" options={['OUI', 'NON']} />
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span className="text-xs">Dépoussiérage / Nettoyage par aspiration (antidéfla.)</span>
                  <YesNoNa value={getYn('atex_depoussierage', 'N/A')} onChange={(v) => setExtra('atex_depoussierage', v)} testid="pf-atex-depoussierage" />
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span className="text-xs">Arrosage / pulvérisation</span>
                  <YesNoNa value={getYn('atex_arrosage', 'N/A')} onChange={(v) => setExtra('atex_arrosage', v)} testid="pf-atex-arrosage" />
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span className="text-xs">Inertage (risque anoxie/asphyxie)</span>
                  <YesNoNa value={getYn('atex_inertage', 'N/A')} onChange={(v) => setExtra('atex_inertage', v)} testid="pf-atex-inertage" />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Autres mesures */}
        <div>
          <div className="text-[10px] font-bold uppercase tracking-wider text-stone-600 mb-1">
            Autres mesures de prévention <span className="font-normal italic normal-case text-[9px]">(Nettoyage de la zone, écran, absorbant, sable, etc…)</span>
          </div>
          <textarea
            value={extra.ext_autres_mesures || ''}
            onChange={(e) => setExtra('ext_autres_mesures', e.target.value)}
            data-testid="pf-autres-mesures"
            rows={2}
            placeholder="Préciser les autres mesures de prévention mises en place…"
            className="w-full px-2 py-1.5 text-xs border border-stone-300 focus:border-[#09090B] outline-none resize-y"
          />
        </div>

        {/* Détection incendie */}
        <div className="flex items-center justify-between gap-3 p-3 bg-red-50 border-l-4 border-[#DA291C]">
          <span className="text-xs font-semibold">Désactivation des systèmes de détection incendie s'ils existent</span>
          <YesNoNa value={getYn('desact_detection_incendie', 'N/A')} onChange={(v) => setExtra('desact_detection_incendie', v)} testid="pf-desact-detection" />
        </div>

        {/* Surveillant feu + Validateur Permis de Feu */}
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <FormationPicker
              module="permis_feu"
              site={siteCode}
              value={extra.surveillant_feu || ''}
              onChange={(name) => setExtra('surveillant_feu', name)}
              testid="pf-surveillant-feu"
              placeholder="Surveillant de feu désigné"
            />
            <div className="mt-1 text-[10px] text-stone-500 italic flex items-start gap-1">
              <Check className="w-3 h-3 mt-0.5 flex-shrink-0" />
              Le surveillant reste sur place au moins 30 min après fin des travaux.
            </div>
          </div>
          <div>
            <FormationPicker
              module="permis_feu"
              site={siteCode}
              value={extra.validateur_pf || ''}
              onChange={(name) => setExtra('validateur_pf', name)}
              testid="pf-validateur"
              placeholder="Validateur Permis de Feu (signataire)"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
